import {DropdownSelectData} from "app/Interface/Component/Form"
import {
  InputSelectBodyProps,
  InputSelectOpenBodyProps,
  InputSelectProps,
  InputSelectSearchProps,
  InputSelectTitleProps
} from "app/Interface/Component/InputSelect"
import EventService from "app/Service/EventService"
import EnvironmentService, {subscribeEnv} from "app/Service/System/EnvironmentService"
import StickyScroll from "app/WebController/Component/Scroll/StickyScroll"
import InputSearch from "app/WebView/Component/Form/InputSearch"
import CloseIcon from "app/WebView/Component/icons/closeIcon"
import DeleteIcon from "app/WebView/Component/icons/deleteIcon"
import cls from "classnames"
import {StickyEvent} from "const/StickyEvent"
import Logger from "logger/logger"
import React, {CSSProperties, useCallback, useEffect, useRef, useState} from "react"
import {ButtonGroup} from "react-bootstrap"
import {useTranslation} from "react-i18next"

function InputSelect(
  {
    id,
    options,
    onChange,

    canOpenFn,
    onOpenCbFn,

    canClear,
    title,

    showSearch,
    variant,

    allowAdd,
    addFn,
  }: InputSelectProps) {

  const {t} = useTranslation('ComponentsForm.InputSelect')

  const {publishEvent} = EventService('InputSelect')

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const toggleOpen = async () => {
    Logger('Element.Input.Select', 'toggleOpen', !isOpen)
    setIsOpen(!isOpen)
    publishEvent<'selectOpen'>(StickyEvent.selectOpen, {isOpen: !isOpen})
    onOpenCbFn && await onOpenCbFn(!isOpen)
  }

  const [activeOptions, setActiveOptions] = useState<DropdownSelectData[]>([])
  const [autoTitle, setAutoTitle] = useState<string>()

  /** Auto title */
  useEffect(function setAutoTitleEffect() {
    if (title) return

    if (undefined === options || options.length < 1) {
      setAutoTitle(t('autoTitle.noOptions'))
      return
    }

    const active = options?.filter(o => o.active) ?? []
    if (active.length > 0)
      setAutoTitle(active.map(o => o.name).join(', '))
    else
      setAutoTitle(t('autoTitle.fallback', {ns: "ComponentsForm.InputDropdown"}))
  }, [options])

  /** Set local active options to show delete button */
  useEffect(function setLocalActiveOptions() {
    // Logger('InputSelect.receivedOptions', options, 'isOpen:', isOpen)
    setActiveOptions(options?.filter(o => o.active) ?? [])
  }, [options])

  const localCanOpenFn = useCallback(() => {
    if (undefined === options) return

    if (typeof canOpenFn === "boolean")
      canOpenFn && options.length > 0 && toggleOpen()
    else
      canOpenFn() && options.length > 0 && toggleOpen()
  }, [canOpenFn, options])

  return (
    <>
      <InputSelectTitle
        id={id}
        onChange={onChange}
        disabled={false}
        handleOpen={localCanOpenFn}
        title={title ?? autoTitle}
        activeOptions={activeOptions}
        variant={variant ?? 'secondary'}
        canClear={canClear ?? true}
      />
      <InputSelectBody
        isOpen={isOpen}
        toggleOpen={toggleOpen}
        onChange={selected => toggleOpen().then(() => onChange(selected))}
        options={options}
        showSearch={showSearch}
        allowAdd={allowAdd}
        addFn={addFn}
      />
    </>
  )
}

export default InputSelect

function InputSelectBody(
  {
    isOpen,
    toggleOpen,
    options,
    onChange,
    showSearch,
    allowAdd,
    addFn,
  }: InputSelectBodyProps) {


  const InputSelectBodyStyles: CSSProperties = {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    display: 'flex',
    flexDirection: 'column'
  }

  const scrollRef = useRef(null)
  const inputSelectRef = useRef<HTMLDivElement>(null)

  const [searchValue, setSearchValue] = useState<string>()
  const [searchOptions, setSearchOptions] = useState<DropdownSelectData[]>([])

  // const [scrollContainerRef, setScrollbarContainerRef] = useState<HTMLElement>()
  const [preSelectIndex, setPreSelectIndex] = useState<number>()

  /** closeOnClickOutsideBody */
  useEffect(function closeOnClickOutsideBody() {
    Logger('InputSelect.isOpen', isOpen)

    // https://stackoverflow.com/questions/62574377/react-modal-close-if-is-clicked-outside-of-content
    function handleWindowClick(event: Event) {
      if (!inputSelectRef.current) return

      const target = event.target as Node
      if (inputSelectRef.current.contains(target))
        return

      toggleOpen()
    }

    // Todo - Fix some bug where menu doesnt want to open again after adding a value (address - city)
    if (isOpen)
      window.addEventListener('mousedown', handleWindowClick)
    else
      window.removeEventListener('mousedown', handleWindowClick)

    return () => {
      window.removeEventListener('mousedown', handleWindowClick)
    }
  }, [isOpen])

  useEffect(() => {
    if (!options) return
    setSearchOptions(options)
  }, [options])

  useEffect(() => {
    if (searchValue)
      setSearchOptions(options?.filter(option => {
        if (option.searchName)
          return option.searchName.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
        else
          return option.name.toLowerCase().indexOf(searchValue.toLowerCase()) > -1
      }) ?? [])
    else
      setSearchOptions(options ?? [])

    setPreSelectIndex(undefined)
  }, [searchValue, isOpen, options])


  function keyDownEvent(e: React.KeyboardEvent<HTMLDivElement>) {
    // Preselect items up and down
    if (e.code === "ArrowDown" || e.code === "ArrowUp") {
      if (undefined === preSelectIndex)
        setPreSelectIndex(0)
      else if (e.code === "ArrowDown" && preSelectIndex < (searchOptions.length - 1)) {
        const newIndex = preSelectIndex + 1
        setPreSelectIndex(newIndex)
        scrollToPreSelectIndex(newIndex)
      } else if (e.code === "ArrowUp" && preSelectIndex > 0) {
        const newIndex = preSelectIndex - 1
        setPreSelectIndex(newIndex)
        scrollToPreSelectIndex(newIndex)
      }
    }

    // Select preSelected on enter
    if (e.code === "Enter") {
      e.preventDefault()  // To prevent any underlying form to be submitted
      undefined !== preSelectIndex && onChange(searchOptions[preSelectIndex])
    }

    // Close on Escape
    if (e.code === "Escape") {
      toggleOpen()
    }
  }

  function scrollToPreSelectIndex(index: number) {
    if (!scrollRef || null === scrollRef.current || !index) return
    // Logger('InputSelectBody.scrollToPreSelectIndex', scrollRef.current._osInstance.scroll({el}))
    // const kids = scrollRef.current._osTargetRef.current.children as HTMLCollection
    // if (kids) {
    //   const osContent = kids.item(3)?.getElementsByClassName('os-content')
    //   if (osContent) {
    //     Logger('InputSelectBody.scrollToPreSelectIndex.osContent', osContent)
    //     osContent.item(index)?.scrollIntoView()
    //     // osContent.children[index].scrollIntoView()
    //   }
    // }
    // Logger('InputSelectBody.scrollToPreSelectIndex', kids, kids.item(3).getElementsByClassName('os-content'))
    // .find(c => {
    //   Logger('InputSelectBody.scrollToPreSelectIndex.child', c)
    // }))
    // const selectedChild = scrollRef.children[index]
    // selectedChild.scrollIntoView(false)
  }

  function handleSearch(v: string | undefined) {
    Logger('InputSelect.handleSearch', v, searchValue)
    if (!v && !searchValue)
      toggleOpen()

    setSearchValue(v)
  }

  return !isOpen || !options || options.length < 1 ? null : (
    <div style={InputSelectBodyStyles} onKeyDown={keyDownEvent} className={`input-select__body`} ref={inputSelectRef}>
      <InputSelectSearch
        showSearch={showSearch}
        searchValue={searchValue}
        searchFn={handleSearch}
        addFn={addFn}
        allowAdd={allowAdd}
        numOptions={searchOptions.length}
      />
      <InputSelectOpenBody
        options={searchOptions}
        onChange={onChange}
        setPreSelectIndex={setPreSelectIndex}
        preSelectIndex={preSelectIndex}
        scrollRef={scrollRef}
        // setScrollbarContainerRef={setScrollbarContainerRef}
      />
    </div>
  )
}

function InputSelectOpenBody(
  {
    options,
    onChange,
    preSelectIndex,
    setPreSelectIndex,
    scrollRef
    // setScrollbarContainerRef
  }: InputSelectOpenBodyProps) {
  const {t} = useTranslation('ComponentsForm.InputSelect')

  return (
    <StickyScroll
      className={`input-select`}
      ref={scrollRef}
    >
      {options.length > 0 ? options.map((option, index) => (
        <div
          key={option.id}
          id={`is-${index}`}
          onMouseMove={() => {
            setPreSelectIndex(index)
          }}
          onMouseEnter={() => {
            if (undefined === preSelectIndex)
              setPreSelectIndex(index)
          }}
          onClick={() => onChange(option)}
          className={cls('input-select__body__option', {'pre-selected': preSelectIndex === index})}
        >
          {option.searchName ?? option.name}
        </div>
      )) : (
        <div
          className={cls('input-select__body__option', 'noClick ')}
        >
          {t('search.noResult')}
        </div>
      )}
    </StickyScroll>
  )
}

function InputSelectSearch(
  {
    showSearch,
    searchValue,
    searchFn,
    allowAdd,
    addFn,
    numOptions,
  }: InputSelectSearchProps) {

  const {t} = useTranslation('ComponentsForm.InputSelect')

  const {isMobileJs} = EnvironmentService([
    subscribeEnv.isMobile
  ], 'InputSelectSearch')

  const wrapperClass = cls(
    `input-select__body__search`,
  )

  function render() {
    if (undefined === isMobileJs)
      return null
    if (showSearch) {
      return (
        <div className={wrapperClass}>
          <InputSearch
            id={'input-select'}
            value={searchValue}
            focus={!isMobileJs}
            onChange={searchFn}
            placeholder={t('search.placeholder')}
          />
          {allowAdd && searchValue && addFn && numOptions < 1 && (
            <div className={`add`} onClick={() => addFn(searchValue)}>{t('addSearchValue', {value: searchValue})}</div>
          )}
        </div>
      )
    } else {
      return (
        <div className={wrapperClass} style={{display: 'flex', flexDirection: 'row'}}>
          <div style={{display: 'flex', flexGrow: 1}}/>
          <CloseIcon
            id={'close'}
            onClick={() => searchFn(undefined)}
            variant={'secondary'}
          />
        </div>
      )
    }
  }

  return render()
}

function InputSelectTitle(
  {
    id,
    onChange,
    activeOptions,
    disabled,
    handleOpen,
    title,
    variant,
    canClear,
  }: InputSelectTitleProps) {

  const buttonClass = cls(
    `btn btn-${variant} sw-select-button`,
    {'disabled': disabled !== undefined ? disabled : false}
  )

  const DeleteIconStyle = {
    flexGrow: 0
  }

  function clearSelection() {
    onChange(undefined)
  }

  function ClearButton() {
    if (activeOptions.length < 1 || !canClear)
      return <></>
    else
      return (
        <DeleteIcon
          id={`delete-${id}`}
          style={DeleteIconStyle}
          variant={'secondary'}
          onClick={() => clearSelection()}
          className={'sw-select'}
        />
      )
  }

  const Button = () => (
    <button
      id={id}
      type={`button`}
      className={buttonClass}
      onClick={() => handleOpen()}
      disabled={disabled !== undefined ? disabled : false}
    >
      {title}
    </button>
  )

  if (canClear) {
    // When using Button() as a function here, it gets detached in Cypress(?)
    return (
      <ButtonGroup>
        <button
          id={id}
          type={`button`}
          className={buttonClass}
          onClick={() => handleOpen()}
          disabled={disabled !== undefined ? disabled : false}
        >
          {title}
        </button>
        <ClearButton/>
      </ButtonGroup>
    )
  } else {
    return <Button/>
  }
}
